import {
  Button,
  ButtonLink,
  Header,
  Icon,
  Stack,
  StackItem,
  Text
} from '@asgard/ui-library';
import { faCheckCircle as faCheckCircleLight } from '@fortawesome/pro-light-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { HashAnchor } from 'components/HashAnchor';
import Page from 'components/layout/Page';
import Checkbox from 'components/shared/Checkbox';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-scroll';
import { ServiceLead } from 'services/service.lead';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getErrorMessage } from 'utils/errors';
import * as S from './MagnetTechTestsPage.styled';
import {
  MagnetTechTestsForm,
  MagnetTechTestsFormSchema
} from './MagnetTechTestsPage.types';

export const MagnetTechTestsPage: React.FC = () => {
  const { t } = useI18n('magnets');
  const { addErrorToast } = useToasts();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [terms, setTerms] = useState(false);
  const [accept, setAccept] = useState(false);

  const formMethods = useForm<MagnetTechTestsForm>({
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      email: '',
      company: ''
    },
    resolver: zodResolver(MagnetTechTestsFormSchema)
  });

  const reason = formMethods.watch('reason');

  const onSubmit = useCallback(
    async (data: MagnetTechTestsForm) => {
      try {
        ServiceLead.create({
          clientName: data.fullName,
          contactEmail: data.email,
          companyName: data.company,
          landingName: 'magnets-technical-interviews', // Note: landing url slug https://www.getmanfred.com/resource-technical-interviews
          isMql: data.reason === '1' || data.reason === '2',
          createPipedriveLead: true,
          isSubscribeToNewsletter: true
        });

        setIsSubmitted(true);
      } catch (ex) {
        console.error(`[MagnetTechTestsPage] Could not send form data. ${ex}`);

        addErrorToast(
          t('magnets.tech-tests.form.submit-error', {
            error: getErrorMessage(ex)
          })
        );
      }
    },
    [addErrorToast, t]
  );

  return (
    <Page pageVariant="midnight_light">
      <S.StyledWrapperBG>
        <S.StyledWrapper>
          <Stack direction="column" gap="s" className="content">
            <Stack direction="column" gap="s" className="wrapper">
              <Text bold uppercase size="s">
                {t('magnets.tech-tests.content.sub-title')}
              </Text>
              <Header as="h2" size="4xl">
                {t('magnets.tech-tests.content.title')}
              </Header>

              <HtmlTrans
                ns="magnets"
                i18nKey="magnets.tech-tests.content.desc"
              />
            </Stack>
          </Stack>

          <StackItem className="form">
            {!isSubmitted ? (
              <FormProvider {...formMethods}>
                <HashAnchor name="form" />
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <Stack direction="column" gap="4xl">
                    <FormText
                      name="fullName"
                      formGroup={{
                        label: t('magnets.tech-tests.form.full-name.label')
                      }}
                    />

                    <FormText
                      name="email"
                      formGroup={{
                        label: t('magnets.tech-tests.form.email.label')
                      }}
                    />

                    <FormText
                      name="company"
                      formGroup={{
                        label: t('magnets.tech-tests.form.company.label')
                      }}
                    />

                    <FormSelect
                      name="reason"
                      formGroup={{ label: t('magnets.tech-tests.form.reason') }}
                      options={[
                        {
                          label: t('magnets.tech-tests.form.reason0'),
                          value: '0'
                        },
                        {
                          label: t('magnets.tech-tests.form.reason1'),
                          value: '1'
                        },
                        {
                          label: t('magnets.tech-tests.form.reason2'),
                          value: '2'
                        },
                        {
                          label: t('magnets.tech-tests.form.reason3'),
                          value: '3'
                        },
                        {
                          label: t('magnets.tech-tests.form.reason4'),
                          value: '4'
                        }
                      ]}
                    />

                    <Checkbox
                      id="accept"
                      checked={accept}
                      onChange={() => setAccept((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.tech-tests.form.privacy.label'}
                        />
                      }
                    />

                    <Checkbox
                      id="terms"
                      checked={terms}
                      onChange={() => setTerms((prev) => !prev)}
                      label={
                        <HtmlTrans
                          ns="magnets"
                          i18nKey={'magnets.tech-tests.form.terms.label'}
                          components={{
                            termsLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.termsOfUse)}
                                target="_blank"
                              />
                            ),

                            privacyLink: (
                              <LinkText
                                href={getAppUrl(APP_URLS.privacyPolicy)}
                                target="_blank"
                              />
                            )
                          }}
                        />
                      }
                    />

                    <div style={{ display: 'none' }}>
                      {formMethods.formState.isValid
                        ? 'valid: true'
                        : 'valid: false'}
                      {terms ? 'terms: true' : 'terms: false'}
                    </div>

                    <StackItem>
                      <Button
                        label={t('magnets.tech-tests.form.submit')}
                        schema="brand2"
                        startIcon={faCheckCircle}
                        size="xs"
                        isLoading={formMethods.formState.isSubmitting}
                        disabled={
                          !terms ||
                          !accept ||
                          !formMethods.formState.isValid ||
                          reason === '0'
                        }
                      />
                    </StackItem>
                  </Stack>
                </form>
              </FormProvider>
            ) : (
              <Stack direction="column" className="success" alignment="center">
                <Icon icon={faCheckCircleLight} size="6xl" />

                <Header as="h2" size="2xl">
                  {t('magnets.tech-tests.success.title')}
                </Header>

                <Text bold size="l">
                  {t('magnets.tech-tests.success.sub-title')}
                </Text>

                <HtmlTrans
                  ns="magnets"
                  i18nKey="magnets.tech-tests.success.desc"
                  components={{
                    teamLink: (
                      <a href="mailto:team@getmanfred.com" target="_blank" />
                    )
                  }}
                />
              </Stack>
            )}
          </StackItem>
        </S.StyledWrapper>
      </S.StyledWrapperBG>
      <S.StyledMktText>
        <Text bold uppercase size="s">
          {t('magnets.tech-tests.content.sub-title')}
        </Text>
        <Header as="h2" size="2xl">
          {t('magnets.tech-tests.mkt.title')}
        </Header>

        <div>
          <HtmlTrans ns="magnets" i18nKey="magnets.tech-tests.mkt.desc" />
        </div>

        <Link to="form">
          <ButtonLink
            label={t('magnets.tech-tests.form.submit')}
            schema="brand2"
            variant="hard"
            startIcon={faCheckCircle}
            size="xs"
          />
        </Link>
      </S.StyledMktText>
    </Page>
  );
};
